import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { Vehicle } from '@/interfaces/vehicle'

import APIServices from '../../api/APIServices'

export interface DeliveryPerson {
  id: number
  username: string
  displayName: string
  email: string
  isActive: boolean
  businessId: number
  avatar: string | null
  lastLogin: string
  roles: {
    name: string
    code: string
  }[]
  vehicles?: Partial<Vehicle>[]
}

// Create a unique, descriptive key for this query
const DELIVERY_PERSONNEL_QUERY_KEY = 'delivery-personnel'

type DeliveryPersonnelResponse = DeliveryPerson[]

/**
 * Fetch delivery personnel from the API
 * Separating API call logic from the hook for better testability
 */
const fetchDeliveryPersonnel = async ({
  includeVehicles,
}: {
  includeVehicles: boolean
}): Promise<DeliveryPersonnelResponse> => {
  const response = await APIServices.get('/invoice/deliverers/all', {
    params: {
      includeVehicles,
    },
  })
  return response.data
}

/**
 * Custom hook for fetching delivery personnel (repartidores)
 *
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useDeliveryPersonnelQuery = ({
  businessId,
  options,
  includeVehicles = false,
}: {
  businessId: number
  options?: Partial<UseQueryOptions<DeliveryPersonnelResponse, AxiosError>>
  includeVehicles?: boolean
}) => {
  // Query hook
  const queryResult = useQuery<DeliveryPersonnelResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [DELIVERY_PERSONNEL_QUERY_KEY, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchDeliveryPersonnel({ includeVehicles }),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    deliveryPersonnel: queryResult.data,
    isLoadingDeliveryPersonnel: queryResult.isLoading,
    isErrorDeliveryPersonnel: queryResult.isError,
    errorDeliveryPersonnel: queryResult.error,
    refetchDeliveryPersonnel: queryResult.refetch,
    isFetchingDeliveryPersonnel: queryResult.isFetching,
  }
}

/**
 * Helper function to get the delivery personnel query key
 * Useful for cache invalidation or prefetching
 */
export const getDeliveryPersonnelQueryKey = (businessId: number) => [
  DELIVERY_PERSONNEL_QUERY_KEY,
  businessId,
]
